import { cn } from '@heidi/utils';

/**
 * Clinician Max tier palette — skies/600 primary fill with matching muted, ink, and border steps.
 *
 * Mapping: fill skies/600, hover skies/700, muted skies/50 (dark: skies/900),
 * ink skies/800 (dark: skies/200), border skies/200 (dark: skies/800).
 */
const CLINICIAN_MAX_FILL_BG =
  'bg-skies-600 hover:bg-skies-700 active:bg-skies-700 newBrandDark:bg-skies-600 newBrandDark:hover:bg-skies-700 newBrandDark:active:bg-skies-700';
const CLINICIAN_MAX_FILL_BG_ONLY = 'bg-skies-600 newBrandDark:bg-skies-600';
const CLINICIAN_MAX_MUTED_BG = 'bg-skies-50 newBrandDark:bg-skies-900';
const CLINICIAN_MAX_TEXT = 'text-skies-800 newBrandDark:text-skies-200';
/** Fixed light ink on theme-locked blue fill — do not use theme-flipping text tokens here. */
const CLINICIAN_MAX_ON_FILL_INK = 'text-text-primary-on-dark';

/** Assertable smoke-test class fragments for unit tests. */
export const CLINICIAN_MAX_SMOKE_TEST_FILL_BG_CLASS = 'bg-skies-600';
export const CLINICIAN_MAX_SMOKE_TEST_MUTED_BG_CLASS = 'bg-skies-50';
export const CLINICIAN_MAX_SMOKE_TEST_TEXT_CLASS = 'text-skies-800';
export const CLINICIAN_MAX_SMOKE_TEST_BORDER_CLASS = 'border-skies-200';
export const CLINICIAN_MAX_SMOKE_TEST_ON_FILL_INK_CLASS = 'text-text-primary-on-dark';
export const CLINICIAN_MAX_SMOKE_TEST_TRIGGER_ICON_CLASS = '!text-text-primary-on-dark';

/** Figma Agents credit surfaces — Clinician Max tier fill and ink. */
export const CLINICIAN_MAX_UPGRADE_BUTTON_CLASS = cn(
  CLINICIAN_MAX_FILL_BG,
  CLINICIAN_MAX_ON_FILL_INK,
  'disabled:bg-skies-600/50 disabled:text-text-disabled newBrandDark:disabled:bg-skies-600/50'
);

/**
 * Both trigger glyphs pin their colour important: the sidebar's open/active rules
 * (`[&[aria-expanded=true]>svg]:text-text-primary` and friends) tie on specificity
 * with the glyph's own class, so without it the tier colour drops on open.
 */
export const CLINICIAN_MAX_UPGRADE_TRIGGER_ICON_CLASS = '!text-text-primary-on-dark';

/** Sidebar / indicator trigger — blue tile, inverted tier icon (matches popover CTA). */
export const CLINICIAN_MAX_UPGRADE_TRIGGER_BUTTON_CLASS = cn(
  CLINICIAN_MAX_FILL_BG_ONLY,
  'hover:!bg-skies-700 focus-visible:!bg-skies-700 newBrandDark:hover:!bg-skies-700 newBrandDark:focus-visible:!bg-skies-700',
  'active:!bg-skies-700 newBrandDark:active:!bg-skies-700',
  'aria-expanded:!bg-skies-700 aria-expanded:hover:!bg-skies-700 newBrandDark:aria-expanded:!bg-skies-700 newBrandDark:aria-expanded:hover:!bg-skies-700',
  'data-[active=true]:!bg-skies-700 newBrandDark:data-[active=true]:!bg-skies-700'
);

/**
 * Blue ink for a labelled row. Important so the sidebar's active/open text colour
 * doesn't win, and so the glyph survives the same svg specificity tie as the tile.
 */
export const CLINICIAN_MAX_UPGRADE_ROW_INK_CLASS = cn(
  '!text-skies-600 newBrandDark:!text-skies-300',
  '[&_svg]:!text-skies-600 newBrandDark:[&_svg]:!text-skies-300'
);
export const CLINICIAN_MAX_SMOKE_TEST_ROW_INK_CLASS = '!text-skies-600';

/** Credit popover panel — Clinician Max tier fill, primary ink on CTA + progress. */
export const CLINICIAN_MAX_CREDIT_POPOVER_BUTTON_CLASS = cn(
  CLINICIAN_MAX_FILL_BG,
  CLINICIAN_MAX_ON_FILL_INK,
  'disabled:bg-skies-600/50 disabled:text-text-disabled newBrandDark:disabled:bg-skies-600/50'
);

export const CLINICIAN_MAX_CREDIT_POPOVER_PROGRESS_CLASS = '[&>div]:bg-skies-600';

/** Credit-limit banner — skies muted fill, border, ink on icon + title. */
export const CLINICIAN_MAX_UPGRADE_BANNER_CLASS = cn(
  '!border-skies-200 !bg-skies-50 newBrandDark:!border-skies-800 newBrandDark:!bg-skies-900'
);

export const CLINICIAN_MAX_UPGRADE_BANNER_INK_CLASS = CLINICIAN_MAX_TEXT;

/** Complimentary usage banner shell — skies/25 fill with dark-mode flip. */
export const CLINICIAN_MAX_COMPLIMENTARY_BANNER_BG_CLASS =
  'bg-skies-25 newBrandDark:bg-skies-900';

/**
 * Billing-settings complimentary usage Banner — the shell fill plus its border,
 * one skies step lighter than the credit-limit banner. Important because the DS
 * Banner sub-variant sets `bg-surface` / `border-border` at equal specificity.
 */
export const CLINICIAN_MAX_COMPLIMENTARY_BANNER_CLASS =
  '!border-skies-100 !bg-skies-25 newBrandDark:!border-skies-800 newBrandDark:!bg-skies-900';

/** Card nested in the complimentary banner shell — skies border step, not neutral. */
export const CLINICIAN_MAX_COMPLIMENTARY_CARD_BORDER_CLASS =
  'border-skies-200 newBrandDark:border-skies-800';

/**
 * Complimentary usage note — validation/info fill + border with accent/blue ink.
 * Semantic tokens carry their own dark step, unlike the skies-* tier palette above.
 */
export const CLINICIAN_MAX_COMPLIMENTARY_NOTE_CLASS =
  'border-validation-info-border bg-validation-info-muted';

export const CLINICIAN_MAX_COMPLIMENTARY_NOTE_INK_CLASS = 'text-accent-blue';

/** Figma stroke weight for the note's 16px icon. */
export const CLINICIAN_MAX_COMPLIMENTARY_NOTE_ICON_STROKE_WIDTH = 1.2;

/** Upgrade modal badge — muted fill + tier ink. */
export const CLINICIAN_MAX_UPGRADE_BADGE_CLASS = cn(
  CLINICIAN_MAX_MUTED_BG,
  CLINICIAN_MAX_TEXT,
  '[&_svg]:text-skies-800 newBrandDark:[&_svg]:text-skies-200'
);

/** Upgrade modal promo illustration panel background. */
export const CLINICIAN_MAX_UPGRADE_PROMO_PANEL_CLASS = CLINICIAN_MAX_MUTED_BG;

/** Upgrade modal workflow accent circle. */
export const CLINICIAN_MAX_UPGRADE_ACCENT_FILL_CLASS = CLINICIAN_MAX_FILL_BG_ONLY;
